import React, { useState, useEffect } from 'react';
import ListContainer from '../components/ListContainer';
import FormContainer from '../components/FormContainer';
import API_BASE_URL from '../config/api';

const ROLE_OPTIONS = [
  'Secretary',
  'Inspector',
  'AR',
  'DR',
  'RCS',
  'Power'
];

const UserListPage = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedRole, setSelectedRole] = useState('ALL');

  // View mode: 'list' | 'create' | 'edit'
  const [viewMode, setViewMode] = useState('list');
  const [editingUserId, setEditingUserId] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: ''
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/users`);
      const data = await res.json();
      if (data.success && Array.isArray(data.users)) {
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenCreateView = () => {
    setFormErrors({});
    setEditingUserId(null);
    setFormData({
      name: '',
      email: '',
      role: '',
      phone: ''
    });
    setViewMode('create');
  };

  const handleOpenEditView = (u) => {
    setFormErrors({});
    setEditingUserId(u._id);
    setFormData({
      name: u.Name || u.name || '',
      email: u.EmailID || u.email || '',
      role: u.Role || u.role || '',
      phone: (u.PhoneNo || u.phone || '').toString()
    });
    setViewMode('edit');
  };

  const handleSaveUser = async () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Full Name is required';
    if (!formData.email.trim()) errors.email = 'Email ID is required';
    if (!formData.role.trim()) errors.role = 'Role is required';
    if (!formData.phone.trim()) errors.phone = 'Phone Number is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setFormSaving(true);

    try {
      const isEdit = viewMode === 'edit' && editingUserId;
      const url = isEdit
        ? `${API_BASE_URL}/api/auth/users/${editingUserId}`
        : `${API_BASE_URL}/api/auth/users`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setViewMode('list');
        setEditingUserId(null);
        fetchUsers();
      } else {
        alert(data.message || 'Failed to save user');
      }
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Error saving user');
    } finally {
      setFormSaving(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const roleMatch = selectedRole === 'ALL' || (u.Role || u.role || '').toLowerCase() === selectedRole.toLowerCase();
    if (!roleMatch) return false;

    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      (u.Name || u.name || '').toLowerCase().includes(term) ||
      (u.EmailID || u.email || '').toLowerCase().includes(term) ||
      (u.Role || u.role || '').toLowerCase().includes(term) ||
      (u.PhoneNo || u.phone || '').toString().includes(term)
    );
  });

  // Pagination States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedRole('ALL');
    setPage(1);
  };

  // Calculate Paginated Chunk
  const totalItems = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * limit;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + limit);

  const handleExportCSV = () => {
    if (filteredUsers.length === 0) return;
    const headers = ["Name", "Email ID", "Role", "Phone Number"];
    const csvRows = filteredUsers.map(u => [
      `"${u.Name || u.name || ''}"`,
      `"${u.EmailID || u.email || ''}"`,
      `"${u.Role || u.role || ''}"`,
      `"${u.PhoneNo || u.phone || ''}"`
    ].join(','));

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...csvRows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `User_List_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render Form View directly on page using FormContainer
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <FormContainer
        title={viewMode === 'edit' ? 'Edit User Details' : 'Add New User'}
        onClose={() => {
          setViewMode('list');
          setEditingUserId(null);
        }}
        onSave={handleSaveUser}
        onCancel={() => {
          setViewMode('list');
          setEditingUserId(null);
        }}
        saving={formSaving}
        saveText={viewMode === 'edit' ? 'Save Changes' : 'Create User'}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '10px' }}>
          
          {/* Full Name */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Full Name *
            </label>
            <input
              type="text"
              placeholder="Enter Full Name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) setFormErrors({ ...formErrors, name: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.name ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {formErrors.name && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.name}</div>}
          </div>

          {/* Email ID */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Email ID *
            </label>
            <input
              type="email"
              placeholder="Enter Email ID (e.g. user@cs.com)"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (formErrors.email) setFormErrors({ ...formErrors, email: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.email ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {formErrors.email && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.email}</div>}
          </div>

          {/* Role Dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              User Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => {
                setFormData({ ...formData, role: e.target.value });
                if (formErrors.role) setFormErrors({ ...formErrors, role: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.role ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#ffffff',
                outline: 'none'
              }}
            >
              <option value="">Select Role</option>
              {ROLE_OPTIONS.map((r, i) => (
                <option key={i} value={r}>{r}</option>
              ))}
            </select>
            {formErrors.role && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.role}</div>}
          </div>

          {/* Phone Number */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
              Phone Number *
            </label>
            <input
              type="text"
              placeholder="Enter Phone Number"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                if (formErrors.phone) setFormErrors({ ...formErrors, phone: null });
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: formErrors.phone ? '1px solid #dc2626' : '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
            {formErrors.phone && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{formErrors.phone}</div>}
          </div>

        </div>
      </FormContainer>
    );
  }

  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by Name, Email, Phone..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            fontSize: '13px',
            width: '260px',
            outline: 'none'
          }}
        />

        {/* Role Filter Dropdown */}
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            fontSize: '13px',
            background: '#ffffff',
            outline: 'none',
            cursor: 'pointer'
          }}
        >
          <option value="ALL">All Roles</option>
          {ROLE_OPTIONS.map((r, i) => (
            <option key={i} value={r}>{r}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleSearchSubmit}
          style={{
            background: '#f59e0b',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 18px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Search
        </button>
        <button
          type="button"
          onClick={handleClearFilters}
          style={{
            background: '#f1f5f9',
            color: '#475569',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      </div>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleOpenCreateView}
          style={{
            background: '#2563eb',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          ➕ Add New User
        </button>

        <button
          type="button"
          onClick={handleExportCSV}
          disabled={filteredUsers.length === 0}
          style={{
            background: filteredUsers.length > 0 ? '#10b981' : '#cbd5e1',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: filteredUsers.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          📥 Export CSV
        </button>
      </div>
    </div>
  );

  // Pagination Footer Component
  const paginationFooter = !loading && totalItems > 0 ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
        Showing {startIndex + 1} to {Math.min(startIndex + limit, totalItems)} of {totalItems} users
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Per Page Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Per page:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              outline: 'none',
              background: '#ffffff'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: currentPage === 1 ? '#f1f5f9' : '#ffffff',
              color: currentPage === 1 ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              fontWeight: '600',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>

          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '600', padding: '0 4px' }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
              color: currentPage === totalPages ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              fontWeight: '600',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <ListContainer title="User List" filterBar={filterBar} footer={paginationFooter}>
      {loading ? (
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <div>Loading Users...</div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          No users found matching your search.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', maxWidth: '100%', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#334155' }}>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>#</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Name</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Email ID</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Role</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Phone Number</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((u, idx) => (
                <tr
                  key={u._id || idx}
                  style={{
                    borderBottom: '1px solid #e2e8f0',
                    background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'
                  }}
                >
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{startIndex + idx + 1}</td>
                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>
                    {u.Name || u.name || '-'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>
                    {u.EmailID || u.email || '-'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span
                      style={{
                        background: '#e0e7ff',
                        color: '#4318ff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                      }}
                    >
                      {u.Role || u.role || 'User'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>
                    {u.PhoneNo || u.phone || '-'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => handleOpenEditView(u)}
                      style={{
                        background: '#f1f5f9',
                        color: '#2563eb',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListContainer>
  );
};

export default UserListPage;
